import React from 'react'

import dynamic from 'next/dynamic'
import {palette} from 'styled-tools'
import styled from 'styled-components'
import {useMedia} from 'react-use'
import {Container, media} from 'styled-bootstrap-grid'

import {H1} from '@festi/common/components/typography'
import {gridTheme} from '@festi/common/themes'
import {useScrollRestoration} from '@festi/common/utils/useScrollRestoration'

import {useAlgoliaContext} from './context/AlgoliaContext'
import CustomHits from './components/CustomHits'
import CustomPagination from './components/CustomPagination'
import SearchGridToggleGroup from './components/SearchGridToggleGroup'
import SubCategoryList from './components/SubCategoryList'

const SelectSize = dynamic(() => import('./components/SelectSize'), {
  ssr: false,
})

const SortDropdown = dynamic(() => import('./components/SortDropdown'), {
  ssr: false,
})

const CatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding-block: 8px;

  ${media.md`
    padding-block: 12px;
  `}
`

const Title = styled(H1)`
  font-size: 24px;
  line-height: 0.9;

  span {
    font-size: 16px;
    font-weight: 500;
    color: ${palette('ui30Solid')};
  }

  ${media.md`
    font-size: 40px;

    span {
      font-size: 24px;
    }
  `}
`

const HeaderRightSectionWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  white-space: nowrap;
`

const DropdownWidth = styled.div`
  width: auto;

  ${media.md`
    width: 320px;
  `}
`

const SearchFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  padding-top: 24px;

  ${media.md`
    flex-direction: row;
  `}
`

const SizeWrapper = styled.div`
  width: 100%;

  ${media.md`
    max-width: 340px;
  `}
`

export interface Props {
  id?: number
  title: string
  attrNameMap: Record<string, string>
}

export default function CategoryResults({id, title, attrNameMap}: Props) {
  const {numberOfHits} = useAlgoliaContext()

  const isMobile = useMedia(`(max-width: ${gridTheme.breakpoints.md}px)`, false)

  useScrollRestoration({
    enabled: true,
    key: 'productListScrollPosition',
    linkSelector: 'a[href*="/vorur/"]',
  })

  return (
    <Container>
      <CatHeader>
        <Title>
          {title}
          <span>&nbsp;({numberOfHits})</span>
        </Title>

        <HeaderRightSectionWrapper>
          <DropdownWidth>
            <SortDropdown iconOnly={isMobile} />
          </DropdownWidth>

          {!isMobile && <SearchGridToggleGroup />}
        </HeaderRightSectionWrapper>
      </CatHeader>

      <SubCategoryList categoryId={id} />

      <CustomHits attrNameMap={attrNameMap} />

      <SearchFooter>
        <div>
          <CustomPagination />
        </div>

        <SizeWrapper>
          <SelectSize />
        </SizeWrapper>
      </SearchFooter>
    </Container>
  )
}
